import { useState, useMemo } from 'react';
import { stockItems, type StockItem, type StockCategory } from '@/mocks/stock';

const stockCategories: StockCategory[] = ["Kozmetik", "Temizlik", "Masaj Yağı", "Tekstil", "Tırnak Malzemesi", "Diğer"];
const unitOptions = ["adet", "set", "paket", "kg", "lt", "ml", "gr", "metre"];

// Toast
interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

export default function StokPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('Tümü');
  const [showAlertOnly, setShowAlertOnly] = useState(false);
  const [stockList, setStockList] = useState<StockItem[]>(stockItems);
  const [entryModal, setEntryModal] = useState<{ open: boolean; item: StockItem | null; type: 'entry' | 'exit' }>({ open: false, item: null, type: 'entry' });
  const [quantity, setQuantity] = useState(1);
  const [toasts, setToasts] = useState<Toast[]>([]);

  // New product modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState<StockItem | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formCategory, setFormCategory] = useState<StockCategory>('Kozmetik');
  const [formUnit, setFormUnit] = useState('adet');
  const [formSupplier, setFormSupplier] = useState('');
  const [formMinStock, setFormMinStock] = useState('');
  const [formCurrentStock, setFormCurrentStock] = useState('');
  const [formUnitPrice, setFormUnitPrice] = useState('');
  const [formNotes, setFormNotes] = useState('');

  const showToast = (message: string, type: 'success' | 'error') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
  };

  const openModal = (item?: StockItem) => {
    if (item) {
      setEditItem(item);
      setFormName(item.name);
      setFormCategory(item.category);
      setFormUnit(item.unit);
      setFormSupplier(item.supplier);
      setFormMinStock(item.minStock.toString());
      setFormCurrentStock(item.currentStock.toString());
      setFormUnitPrice(item.unitPrice.toString());
      setFormNotes('');
    } else {
      setEditItem(null);
      setFormName('');
      setFormCategory('Kozmetik');
      setFormUnit('adet');
      setFormSupplier('');
      setFormMinStock('');
      setFormCurrentStock('');
      setFormUnitPrice('');
      setFormNotes('');
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditItem(null);
  };

  const handleSave = () => {
    if (!formName.trim() || !formSupplier.trim() || !formMinStock || !formCurrentStock || !formUnitPrice) {
      showToast('Lütfen ürün adı, tedarikçi, min. stok, mevcut stok ve birim fiyat alanlarını doldurun.', 'error');
      return;
    }

    const today = '2026-05-08';
    const payload: StockItem = {
      id: editItem ? editItem.id : `stk-${Date.now()}`,
      name: formName.trim(),
      category: formCategory,
      currentStock: Number(formCurrentStock),
      minStock: Number(formMinStock),
      unit: formUnit,
      supplier: formSupplier.trim(),
      lastEntryDate: editItem ? editItem.lastEntryDate : today,
      lastExitDate: editItem ? editItem.lastExitDate : '-',
      entryQuantity: editItem ? editItem.entryQuantity : Number(formCurrentStock),
      exitQuantity: editItem ? editItem.exitQuantity : 0,
      alert: Number(formCurrentStock) <= Number(formMinStock),
      unitPrice: Number(formUnitPrice),
    };

    if (editItem) {
      setStockList(prev => prev.map(s => s.id === editItem.id ? payload : s));
      showToast('Ürün başarıyla güncellendi.', 'success');
    } else {
      setStockList(prev => [payload, ...prev]);
      showToast('Yeni ürün başarıyla eklendi.', 'success');
    }
    closeModal();
  };

  const handleDelete = (id: string) => {
    if (confirm('Bu ürünü silmek istediğinize emin misiniz?')) {
      setStockList(prev => prev.filter(s => s.id !== id));
      showToast('Ürün silindi.', 'success');
    }
  };

  const filtered = useMemo(() => {
    let data = [...stockList];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      data = data.filter(s => s.name.toLowerCase().includes(q) || s.supplier.toLowerCase().includes(q));
    }
    if (categoryFilter !== 'Tümü') data = data.filter(s => s.category === categoryFilter);
    if (showAlertOnly) data = data.filter(s => s.alert || s.currentStock <= s.minStock);
    return data;
  }, [stockList, searchQuery, categoryFilter, showAlertOnly]);

  const handleStockUpdate = () => {
    if (!entryModal.item || quantity <= 0) return;
    setStockList(prev => prev.map(item => {
      if (item.id !== entryModal.item!.id) return item;
      if (entryModal.type === 'entry') {
        return { ...item, currentStock: item.currentStock + quantity, entryQuantity: item.entryQuantity + quantity, lastEntryDate: '2026-05-08', alert: (item.currentStock + quantity) <= item.minStock };
      }
      return { ...item, currentStock: Math.max(0, item.currentStock - quantity), exitQuantity: item.exitQuantity + quantity, lastExitDate: '2026-05-08', alert: (item.currentStock - quantity) <= item.minStock };
    }));
    showToast(entryModal.type === 'entry' ? 'Stok girişi yapıldı.' : 'Stok çıkışı yapıldı.', 'success');
    setEntryModal({ open: false, item: null, type: 'entry' });
    setQuantity(1);
  };

  const stats = useMemo(() => ({
    total: stockList.length,
    low: stockList.filter(s => s.currentStock <= s.minStock).length,
    totalValue: stockList.reduce((sum, s) => sum + s.currentStock * s.unitPrice, 0),
  }), [stockList]);

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Toast notifications */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map(t => (
          <div key={t.id} className="px-4 py-3 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2 animate-slide-in" style={{ background: t.type === 'success' ? '#10B981' : '#EF4444', color: '#fff' }}>
            <div className="w-4 h-4 flex items-center justify-center"><i className={t.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} /></div>
            {t.message}
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold font-display" style={{ color: 'var(--text-primary)' }}>Stok</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{filtered.length} stok kaydı</p>
        </div>
        <button onClick={() => openModal()} className="spa-btn-primary text-sm flex items-center gap-2 self-start sm:self-auto">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
          Yeni Ürün
        </button>
      </div>

      {/* Alert Banner */}
      {stats.low > 0 && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-spa-sm text-xs font-medium" style={{ background: 'rgba(239,68,68,0.08)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.15)' }}>
          <i className="ri-error-warning-line" /> {stats.low} ürün kritik stok seviyesinin altında! Tedarik edilmeli.
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
          { label: 'Toplam Ürün', value: stats.total, icon: 'ri-stack-line', color: '#C9A962' },
          { label: 'Kritik Stok', value: stats.low, icon: 'ri-error-warning-line', color: '#EF4444' },
          { label: 'Stok Değeri', value: `₺${stats.totalValue.toLocaleString('tr-TR')}`, icon: 'ri-coins-line', color: '#10B981' },
        ].map(s => (
          <div key={s.label} className="spa-card p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-spa flex items-center justify-center flex-shrink-0" style={{ background: s.color + '12' }}>
              <div className="w-5 h-5 flex items-center justify-center"><i className={`${s.icon} text-lg`} style={{ color: s.color }} /></div>
            </div>
            <div>
              <p className="text-lg font-bold font-display" style={{ color: 'var(--text-primary)' }}>{s.value}</p>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-search-line text-sm" style={{ color: 'var(--text-muted)' }} /></div>
          <input className="spa-input pl-10" placeholder="Ürün adı veya tedarikçi ara..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>
        <div className="flex gap-2 flex-wrap">
          <select className="spa-input text-sm min-w-[120px]" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
            <option>Tümü</option>
            {stockCategories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button
            onClick={() => setShowAlertOnly(!showAlertOnly)}
            className="px-3 py-2 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-all"
            style={{
              background: showAlertOnly
                ? 'linear-gradient(135deg, var(--gold), var(--gold-dark))'
                : 'var(--bg-tertiary)',
              color: showAlertOnly ? '#fff' : 'var(--text-secondary)',
            }}
          >
            <i className="ri-error-warning-line mr-1" />Kritik Stok
          </button>
        </div>
      </div>

      {/* Stock Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {filtered.map(item => {
          const isLow = item.currentStock <= item.minStock;
          return (
            <div key={item.id} className="spa-card p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{item.name}</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{item.category} | {item.supplier}</p>
                </div>
                {isLow && (
                  <span className="spa-badge text-[10px]" style={{ background: 'rgba(239,68,68,0.12)', color: '#EF4444' }}>
                    <i className="ri-error-warning-line mr-1" />Kritik
                  </span>
                )}
              </div>

              <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: 'var(--bg-tertiary)' }}>
                <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, (item.currentStock / (item.minStock * 3)) * 100)}%`, background: isLow ? '#EF4444' : 'var(--gold)' }} />
              </div>

              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                  <p className="text-sm font-bold" style={{ color: isLow ? '#EF4444' : 'var(--text-primary)' }}>{item.currentStock}</p>
                  <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Mevcut</p>
                </div>
                <div className="p-2 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                  <p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{item.minStock}</p>
                  <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Min. Stok</p>
                </div>
                <div className="p-2 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                  <p className="text-sm font-bold" style={{ color: 'var(--gold)' }}>₺{item.unitPrice}</p>
                  <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Birim</p>
                </div>
              </div>

              <div className="flex items-center justify-between text-xs" style={{ color: 'var(--text-muted)' }}>
                <span>Son giriş: {item.lastEntryDate}</span>
                <span>Son çıkış: {item.lastExitDate}</span>
              </div>

              <div className="flex gap-2 pt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
                <button onClick={() => openModal(item)} className="w-7 h-7 rounded-md flex items-center justify-center cursor-pointer" style={{ background: 'rgba(59,130,246,0.12)' }} title="Düzenle">
                  <i className="ri-pencil-line text-xs" style={{ color: '#3B82F6' }} />
                </button>
                <button onClick={() => { setEntryModal({ open: true, item, type: 'entry' }); setQuantity(1); }} className="flex-1 spa-btn-secondary text-xs py-2">
                  <i className="ri-add-line mr-1" />Giriş
                </button>
                <button onClick={() => { setEntryModal({ open: true, item, type: 'exit' }); setQuantity(1); }} className="flex-1 spa-btn-secondary text-xs py-2">
                  <i className="ri-subtract-line mr-1" />Çıkış
                </button>
                <button onClick={() => handleDelete(item.id)} className="w-7 h-7 rounded-md flex items-center justify-center cursor-pointer" style={{ background: 'rgba(239,68,68,0.12)' }} title="Sil">
                  <i className="ri-delete-bin-line text-xs" style={{ color: '#EF4444' }} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 space-y-3">
          <div className="w-14 h-14 flex items-center justify-center rounded-full" style={{ background: 'var(--bg-tertiary)' }}>
            <i className="ri-stack-line text-xl" style={{ color: 'var(--text-muted)' }} />
          </div>
          <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Stok kaydı bulunamadı</p>
        </div>
      )}

      {/* Entry/Exit Modal */}
      {entryModal.open && entryModal.item && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0" style={{ background: 'var(--modal-overlay)' }} onClick={() => setEntryModal({ open: false, item: null, type: 'entry' })} />
          <div className="relative bg-theme-card rounded-spa w-full max-w-sm animate-scale-in border p-5 space-y-4" style={{ borderColor: 'var(--card-border)' }}>
            <h3 className="text-base font-semibold font-display" style={{ color: 'var(--text-primary)' }}>
              {entryModal.type === 'entry' ? 'Stok Girişi' : 'Stok Çıkışı'}: {entryModal.item.name}
            </h3>
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Miktar ({entryModal.item.unit})</label>
              <input type="number" min={1} className="spa-input" value={quantity} onChange={e => setQuantity(Number(e.target.value))} />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEntryModal({ open: false, item: null, type: 'entry' })} className="flex-1 spa-btn-secondary text-sm">İptal</button>
              <button onClick={handleStockUpdate} className="flex-1 spa-btn-primary text-sm">Onayla</button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Product Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.45)' }} onClick={closeModal}>
          <div className="spa-card w-full max-w-lg flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b flex-shrink-0" style={{ borderColor: 'var(--border-color)' }}>
              <h2 className="text-lg font-bold font-display" style={{ color: 'var(--text-primary)' }}>{editItem ? 'Ürünü Düzenle' : 'Yeni Ürün Ekle'}</h2>
              <button onClick={closeModal} className="w-8 h-8 flex items-center justify-center rounded-md cursor-pointer" style={{ background: 'var(--bg-tertiary)' }}><i className="ri-close-line" style={{ color: 'var(--text-muted)' }} /></button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto flex-1 min-h-0">
              <div>
                <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Ürün Adı <span style={{ color: '#EF4444' }}>*</span></label>
                <input className="spa-input text-sm" placeholder="Örn. Lavanta Uçucu Yağı 100ml" value={formName} onChange={e => setFormName(e.target.value)} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Kategori <span style={{ color: '#EF4444' }}>*</span></label>
                  <select className="spa-input text-sm" value={formCategory} onChange={e => setFormCategory(e.target.value as StockCategory)}>
                    {stockCategories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Birim <span style={{ color: '#EF4444' }}>*</span></label>
                  <select className="spa-input text-sm" value={formUnit} onChange={e => setFormUnit(e.target.value)}>
                    {unitOptions.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Tedarikçi / Satıcı <span style={{ color: '#EF4444' }}>*</span></label>
                <input className="spa-input text-sm" placeholder="Örn. Aroma Doğal Ürünler" value={formSupplier} onChange={e => setFormSupplier(e.target.value)} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Mevcut Stok <span style={{ color: '#EF4444' }}>*</span></label>
                  <input type="number" className="spa-input text-sm" placeholder="0" value={formCurrentStock} onChange={e => setFormCurrentStock(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Min. Stok <span style={{ color: '#EF4444' }}>*</span></label>
                  <input type="number" className="spa-input text-sm" placeholder="0" value={formMinStock} onChange={e => setFormMinStock(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Birim Fiyat (₺) <span style={{ color: '#EF4444' }}>*</span></label>
                  <input type="number" className="spa-input text-sm" placeholder="0" value={formUnitPrice} onChange={e => setFormUnitPrice(e.target.value)} />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Notlar</label>
                <textarea className="spa-input text-sm min-h-[80px] resize-none" placeholder="Ek notlar..." maxLength={500} value={formNotes} onChange={e => setFormNotes(e.target.value)} />
                <p className="text-[10px] mt-1 text-right" style={{ color: 'var(--text-muted)' }}>{formNotes.length}/500</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t flex-shrink-0" style={{ borderColor: 'var(--border-color)' }}>
              <button onClick={closeModal} className="spa-btn-outline text-sm">İptal</button>
              <button onClick={handleSave} className="spa-btn-primary text-sm">{editItem ? 'Güncelle' : 'Kaydet'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}